import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Plug, Search, Zap, MapPin, Loader2, Navigation } from "lucide-react";
import { crm } from "@/lib/crm";
import { isEV, isPureElectric } from "@/data/ev";

const carIcon = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-100%)"><div style="background:#0B132B;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,.5);border:2px solid #fff"></div></div>`,
  iconSize: [26, 26], iconAnchor: [0, 0],
});
const stationIcon = (fast, dim) => L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-100%);opacity:${dim ? 0.4 : 1}"><div style="background:${dim ? "#3a4557" : (fast ? "#39FF6A" : "#64748b")};width:24px;height:24px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 0 12px ${fast && !dim ? "rgba(57,255,106,.8)" : "rgba(0,0,0,.4)"};border:2px solid #0B132B"></div></div>`,
  iconSize: [24, 24], iconAnchor: [0, 0],
});

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, map.getZoom() < 10 ? 11 : map.getZoom()); }, [center]); // eslint-disable-line
  return null;
}

export default function EVChargingMap({ car }) {
  const carCenter = (car?.latitude != null && car?.longitude != null) ? [car.latitude, car.longitude] : null;
  const [center, setCenter] = useState(carCenter);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(null);
  const [q, setQ] = useState("");
  const [geoErr, setGeoErr] = useState("");
  const [ready, setReady] = useState(false);
  const debounce = useRef(null);

  useEffect(() => { const id = setTimeout(() => setReady(true), 50); return () => clearTimeout(id); }, []);

  useEffect(() => {
    if (!center) return;
    setLoading(true);
    crm(`/ev/charging-stations?lat=${center[0]}&lng=${center[1]}&distance=40&max_results=40`)
      .then((d) => { setStations(d?.stations || []); setSource(d?.source || null); })
      .catch(() => setStations([]))
      .finally(() => setLoading(false));
  }, [center]);

  const search = () => {
    if (!q.trim()) return;
    setGeoErr("");
    crm(`/geo/be?q=${encodeURIComponent(q.trim())}`)
      .then((d) => { if (d?.lat) setCenter([d.lat, d.lng]); else setGeoErr("Localisation introuvable"); })
      .catch(() => setGeoErr("Localisation introuvable"));
  };

  const [powerMin, setPowerMin] = useState(0);
  const [connFilter, setConnFilter] = useState(null);
  // Power filter removes stations; the connector filter only DIMS incompatible
  // ones (kept visible with an explanatory note), sorted compatible-first.
  const isCompatible = (s) => !connFilter || (s.connectors || []).includes(connFilter);
  const shown = useMemo(() => {
    const list = stations.filter((s) => (!powerMin || (s.power_kw || 0) >= powerMin));
    if (!connFilter) return list;
    return [...list].sort((a, b) => (isCompatible(b) ? 1 : 0) - (isCompatible(a) ? 1 : 0));
  }, [stations, powerMin, connFilter]); // eslint-disable-line
  const connLabel = { CCS: "CCS", CHAdeMO: "CHAdeMO", "Type 2": "Type 2" };
  const availConns = useMemo(() => {
    const set = new Set();
    stations.forEach((s) => (s.connectors || []).forEach((c) => { if (connLabel[c]) set.add(c); }));
    return Object.keys(connLabel).filter((c) => set.has(c));
  }, [stations]);
  const fastCount = useMemo(() => shown.filter((s) => s.fast).length, [shown]);
  const compatCount = useMemo(() => shown.filter(isCompatible).length, [shown, connFilter]); // eslint-disable-line

  // Auto-preselect the connector standard of the displayed vehicle
  // (pure EV → CCS, plug-in hybrid → Type 2), once, if such stations exist.
  const vehicleConnector = isPureElectric(car?.fuel) ? "CCS" : (isEV(car?.fuel) ? "Type 2" : null);
  const autoApplied = useRef(false);
  useEffect(() => {
    if (!autoApplied.current && vehicleConnector && availConns.includes(vehicleConnector)) {
      setConnFilter(vehicleConnector);
      autoApplied.current = true;
    }
  }, [availConns, vehicleConnector]);

  if (!car || !isEV(car.fuel)) return null;

  return (
    <section data-testid="ev-charging-map" className="ev-charging-map theme-electric force-dark mt-6 overflow-hidden rounded-3xl border border-[#39FF6A]/15 bg-[#0B132B] p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#39FF6A]/12 text-[#39FF6A]"><Plug size={18} /></span>
          <div>
            <h2 className="font-display text-lg font-extrabold text-white ev-glow-text">Bornes de recharge à proximité</h2>
            <p className="text-xs text-slate-400">{loading ? "Recherche…" : `${shown.length} borne(s) · ${fastCount} rapide(s)${connFilter ? ` · ${compatCount} compatible(s)` : ""}`}{source === "fallback" && " · réseau partenaire"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MapPin size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="ev-map-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Code postal ou ville…"
              className="h-9 w-44 rounded-lg border border-white/10 bg-white/5 pl-8 pr-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#39FF6A]/60" />
          </div>
          <button data-testid="ev-map-search-btn" onClick={search}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-[#39FF6A]/15 px-3 text-sm font-semibold text-[#86FFA5] transition-colors hover:bg-[#39FF6A]/25">
            <Search size={14} /> Chercher
          </button>
          {carCenter && (
            <button data-testid="ev-map-recenter" onClick={() => setCenter(carCenter)} title="Recentrer sur le véhicule"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-[#39FF6A]/50 hover:text-[#39FF6A]">
              <Navigation size={15} />
            </button>
          )}
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="ev-map-power-filter">
        <span className="mr-1 flex items-center gap-1 text-xs text-slate-400"><Zap size={12} className="text-[#39FF6A]" /> Puissance</span>
        {[[0, "Toutes"], [50, "≥ 50 kW"], [150, "≥ 150 kW"]].map(([v, lbl]) => (
          <button key={v} onClick={() => setPowerMin(v)} data-testid={`ev-map-power-${v}`}
            className={`rounded-lg px-2.5 py-1 text-xs font-bold transition ${powerMin === v ? "bg-[#39FF6A] text-[#0B132B]" : "border border-white/10 text-slate-300 hover:border-[#39FF6A]/50 hover:text-[#39FF6A]"}`}>
            {lbl}
          </button>
        ))}
      </div>
      {availConns.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="ev-map-conn-filter">
          <span className="mr-1 flex items-center gap-1 text-xs text-slate-400"><Plug size={12} className="text-[#00E5FF]" /> Prise</span>
          <button onClick={() => setConnFilter(null)} data-testid="ev-map-conn-all"
            className={`rounded-lg px-2.5 py-1 text-xs font-bold transition ${!connFilter ? "bg-[#00E5FF] text-[#0B132B]" : "border border-white/10 text-slate-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF]"}`}>
            Toutes
          </button>
          {availConns.map((c) => (
            <button key={c} onClick={() => setConnFilter(c)} data-testid={`ev-map-conn-${c.replace(/\s+/g, "")}`}
              className={`rounded-lg px-2.5 py-1 text-xs font-bold transition ${connFilter === c ? "bg-[#00E5FF] text-[#0B132B]" : "border border-white/10 text-slate-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF]"}`}>
              {c}
            </button>
          ))}
          {vehicleConnector && connFilter === vehicleConnector && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-[#39FF6A]" data-testid="ev-map-conn-vehicle-hint">
              <Zap size={11} className="fill-[#39FF6A]" /> prise de ce véhicule
            </span>
          )}
        </div>
      )}
      {geoErr && <p className="mb-2 text-xs text-rose-400" data-testid="ev-map-geo-err">{geoErr}</p>}

      {!center ? (
        <div className="ev-glass rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="ev-map-no-location">
          Localisation du véhicule inconnue. Saisissez votre code postal ou votre ville pour voir les bornes autour de vous.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="h-[340px] overflow-hidden rounded-2xl border border-white/10" data-testid="ev-map-canvas">
            {ready && (
              <MapContainer center={center} zoom={11} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Recenter center={center} />
                {carCenter && <Marker position={carCenter} icon={carIcon}><Popup>Véhicule</Popup></Marker>}
                {shown.map((s) => (
                  <Marker key={s.id} position={[s.lat, s.lng]} icon={stationIcon(s.fast, !isCompatible(s))}>
                    <Popup>
                      <strong>{s.title}</strong><br />
                      {s.operator ? `${s.operator} · ` : ""}{s.power_kw ? `${s.power_kw} kW` : "puissance n.c."}{s.fast ? " · Rapide" : ""}
                      {s.connectors?.length > 0 && <><br />{s.connectors.join(" · ")}</>}
                      {!isCompatible(s) && <><br />⚠️ Prise {connFilter} non disponible ici</>}
                      {s.status && s.status.operational === true && <><br />✅ Disponible</>}
                      {s.status && s.status.operational === false && <><br />⛔ Hors service</>}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>
          <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1" data-testid="ev-map-list">
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-slate-400"><Loader2 size={15} className="animate-spin" /> Chargement…</div>
            ) : shown.length === 0 ? (
              <div className="p-4 text-sm text-slate-400">Aucune borne trouvée{powerMin ? ` ≥ ${powerMin} kW` : ""} dans un rayon de 40 km.</div>
            ) : shown.slice(0, 25).map((s) => {
              const compat = isCompatible(s);
              return (
              <div key={s.id} className={`ev-glass rounded-xl p-3 transition ${compat ? "" : "opacity-45 grayscale"}`} data-testid={`ev-station-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 truncate text-sm font-semibold text-white">
                      {s.fast ? <Zap size={13} className="shrink-0 fill-[#39FF6A] text-[#39FF6A]" /> : <Plug size={13} className="shrink-0 text-slate-400" />}
                      <span className="truncate">{s.title}</span>
                    </div>
                    <div className="truncate text-xs text-slate-400">{s.operator || "Opérateur n.c."}{s.town ? ` · ${s.town}` : ""}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    {s.power_kw && <div className="ev-num text-sm font-bold text-[#86FFA5]">{s.power_kw} kW</div>}
                    {s.distance_km != null && <div className="text-[11px] text-slate-500">{s.distance_km} km</div>}
                  </div>
                </div>
                {(s.connectors?.length > 0 || (s.status && s.status.operational != null)) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`ev-station-meta-${s.id}`}>
                    {(s.connectors || []).map((cc) => (
                      <span key={cc} className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${connFilter && cc === connFilter ? "border-[#39FF6A]/50 bg-[#39FF6A]/15 text-[#39FF6A]" : "border-[#00E5FF]/30 bg-[#00E5FF]/10 text-[#7FE9FF]"}`}>{cc}</span>
                    ))}
                    {s.status && s.status.operational === true && (
                      <span className="ml-auto flex items-center gap-1 rounded-md bg-[#39FF6A]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#39FF6A]" data-testid={`ev-station-status-${s.id}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-[#39FF6A]" /> Disponible
                      </span>
                    )}
                    {s.status && s.status.operational === false && (
                      <span className="ml-auto flex items-center gap-1 rounded-md bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300" data-testid={`ev-station-status-${s.id}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-rose-400" /> Hors service
                      </span>
                    )}
                  </div>
                )}
                {!compat && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-amber-300/90" data-testid={`ev-station-incompat-${s.id}`}>
                    <Plug size={11} /> Prise {connFilter} non disponible — incompatible avec ce véhicule
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      )}
    </section>
  );
}
